import { config } from '../config';

export class RoundHelper {
  private static roundHalfUp(num: number, decimals: number = 2): number {
    const normalized = parseFloat(num.toFixed(15));
    const str = normalized.toString();
    const decimalIndex = str.indexOf('.');
    
    if (decimalIndex === -1) {
      return normalized;
    }
    
    const integerPart = str.substring(0, decimalIndex);
    let decimalPart = str.substring(decimalIndex + 1);
    
    if (decimalPart.length <= decimals) {
      return normalized;
    }
    
    const roundingDigit = parseInt(decimalPart.charAt(decimals));
    const digitsToKeep = decimalPart.substring(0, decimals);
    
    let roundedDecimal: string;
    if (roundingDigit >= 5) {
      const digitsNum = parseInt(digitsToKeep || '0');
      let roundedNum = digitsNum + 1;
      roundedDecimal = roundedNum.toString().padStart(decimals, '0');
      
      if (roundedDecimal.length > decimals) {
        const integerNum = parseInt(integerPart || '0');
        const overflow = Math.floor(roundedNum / Math.pow(10, decimals));
        const newInteger = integerNum + overflow;
        roundedDecimal = (roundedNum % Math.pow(10, decimals)).toString().padStart(decimals, '0');
        return parseFloat(newInteger + '.' + roundedDecimal);
      }
    } else {
      roundedDecimal = digitsToKeep.padEnd(decimals, '0');
    }
    
    return parseFloat(integerPart + '.' + roundedDecimal);
  }

  private static roundABNT(num: number, decimals: number = 2): number {
    const normalized = parseFloat(num.toFixed(Math.max(decimals + 5, 10)));
    const sign = normalized < 0 ? -1 : 1;
    const abs = Math.abs(normalized);
    
    let str = abs.toString();

    if (str.includes('e') || str.includes('E')) {
      str = abs.toString();
    }
    
    if (!str.includes('.')) {
      str += '.';
    }
    
    const [intPart, fracPart] = str.split('.');
    
    let cleanFracPart = fracPart.replace(/0+$/, '');
    
    const needed = decimals + 1;
    if (cleanFracPart.length < needed) {
      cleanFracPart = cleanFracPart.padEnd(needed, '0');
    }

    const decidingDigit = parseInt(cleanFracPart[decimals] || '0');

    const rest = cleanFracPart.substring(decimals + 1);
    const restHasNonZero = /[1-9]/.test(rest);
    
    let roundUp: boolean;
    
    if (decidingDigit > 5) {
      roundUp = true;
    } else if (decidingDigit < 5) {
      roundUp = false;
    } else {
      if (restHasNonZero) {
        roundUp = true;
      } else {
        let prevDigit: number;
        if (decimals === 0) {
          prevDigit = parseInt(intPart.slice(-1) || '0');
        } else {
          prevDigit = parseInt(cleanFracPart[decimals - 1] || '0');
        }
        roundUp = (prevDigit % 2) === 1;
      }
    }
    
    let keepFrac = decimals > 0 ? cleanFracPart.substring(0, decimals) : '';
    if (decimals > 0) {
      keepFrac = keepFrac.padEnd(decimals, '0');
    }
    
    let baseNumberStr = intPart + keepFrac;
    
    const incrementStringInt = (numStr: string): string => {
      let i = numStr.length - 1;
      let carry = 1;
      let res = numStr.split('');
      
      while (i >= 0 && carry > 0) {
        const d = parseInt(res[i]);
        const sum = d + carry;
        res[i] = (sum % 10).toString();
        carry = Math.floor(sum / 10);
        i--;
      }
      
      if (carry > 0) {
        res.unshift('1');
      }
      
      let result = res.join('').replace(/^0+/, '');
      if (result === '') {
        result = '0';
      }
      return result;
    };
    
    if (roundUp) {
      baseNumberStr = incrementStringInt(baseNumberStr);
    }
    
    let resultAbsStr: string;
    if (decimals === 0) {
      resultAbsStr = baseNumberStr;
    } else {
      baseNumberStr = baseNumberStr.padStart(decimals + 1, '0');
      const len = baseNumberStr.length;
      const intPortion = baseNumberStr.substring(0, len - decimals);
      const fracPortion = baseNumberStr.substring(len - decimals);
      resultAbsStr = intPortion + '.' + fracPortion;
    }
    
    const final = parseFloat(resultAbsStr);
    return sign < 0 ? -final : final;
  }

  static toNumber(value?: string | number): number {
    if (value === undefined || value === null || value === '') {
      return 0;
    }
    
    // Se não foi passado roundingType, busca da configuração do cliente
    const type = config.roundingType;
    
    if (typeof value === 'string') {
      const normalizedStr = value.replace(',', '.').trim();
      const num = parseFloat(normalizedStr);
      
      if (isNaN(num)) {
        return 0;
      }
      
      return type === 0 ? this.roundABNT(num, 2) : this.roundHalfUp(num, 2);
    }
    
    if (isNaN(value)) {
      return 0;
    }
    
    return type === 0 ? this.roundABNT(value, 2) : this.roundHalfUp(value, 2);
  }

  static toFormatted(
    value?: string | number,
    locale: string = 'pt-BR',
    currency: string = 'BRL',
  ): string {
    const rounded = this.toNumber(value);
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(rounded);
  }
}
